import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MoveRight, Phone, MessageSquare, UserCog, Hand, Save, Send } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { StageBadge, TagBadge } from "../components/StageBadge";
import StageTransitionDialog from "../components/StageTransitionDialog";
import ScheduleActivityDialog from "../components/ScheduleActivityDialog";
import SendEmailDialog from "../components/SendEmailDialog";
import EmailThreadsView from "../components/EmailThreadsView";
import { api, formatApiError } from "../lib/api";
import { useAuth, normalizeRole } from "../lib/auth";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

const SELLER_TAG_STAGES = ["Agreement In-Process", "Onboarding-in-Process", "Live"];

const SECTIONS = [
  { key: "identity", title: "Identity", fields: ["company_name", "website_url", "category", "seller_tag", "client_type", "lead_source", "lead_date"] },
  { key: "contact", title: "Contact", fields: ["client_poc_name", "poc_designation", "client_poc_email", "client_poc_number"] },
  { key: "classification", title: "Classification", fields: ["lead_stage", "stage_tagging", "lost_reason", "sales_poc", "remarks"] },
  { key: "opportunity", title: "Opportunity", fields: ["opportunity_type", "monthly_shipping_volume", "warehouse_location", "hq_city", "hq_state", "current_wh_partner", "current_lm_partner", "wms_oms"] },
  { key: "campaign", title: "Campaign Attribution (UTM)", fields: ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "referrer"] },
  { key: "financials", title: "Financials & timing", fields: ["monthly_order_potential", "overall_monthly_revenue", "universal_order_potential", "mg_amount", "interested_month", "tentative_live_month", "agreement_initiated_month", "oib_month", "live_month"], restricted: true },
];

const FINANCIAL_FIELDS = new Set([
  "mg_amount", "overall_monthly_revenue", "monthly_order_potential", "universal_order_potential",
]);

const LABELS = {
  company_name: "Company name", website_url: "Website URL", category: "Category",
  seller_tag: "Seller Tag",
  lead_source: "Lead source", client_poc_name: "Client POC name", poc_designation: "POC designation",
  client_poc_email: "POC email", client_poc_number: "POC phone", lead_stage: "Stage",
  lead_date: "Lead date",
  stage_tagging: "Tag", lost_reason: "Lost reason", sales_poc: "Pipeline", remarks: "Remarks",
  opportunity_type: "Opportunity type", warehouse_location: "Warehouse location",
  monthly_shipping_volume: "Monthly shipping volume",
  hq_city: "HQ city", hq_state: "HQ state", current_wh_partner: "Current WH partner",
  current_lm_partner: "Current LM partner", wms_oms: "WMS / OMS",
  utm_source: "UTM Source", utm_medium: "UTM Medium", utm_campaign: "UTM Campaign",
  utm_content: "UTM Content", utm_term: "UTM Term", referrer: "Referrer URL",
  monthly_order_potential: "Monthly order potential", overall_monthly_revenue: "Overall monthly revenue (₹)",
  universal_order_potential: "Universal order potential (B2C+D2C)", mg_amount: "MG amount (₹)",
  interested_month: "Interested month", tentative_live_month: "Tentative live month",
  agreement_initiated_month: "Agreement initiated month", oib_month: "OIB month", live_month: "Live month",
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [tab, setTab] = useState("info");
  const [edits, setEdits] = useState({});
  const [sellerTagError, setSellerTagError] = useState("");
  const [activities, setActivities] = useState([]);
  const [history, setHistory] = useState([]);
  const [audit, setAudit] = useState([]);
  const [stageOpen, setStageOpen] = useState(false);
  const [masters, setMasters] = useState({});
  const [users, setUsers] = useState([]);
  const [callNote, setCallNote] = useState("");
  const [callStatus, setCallStatus] = useState("connected");
  const [noteText, setNoteText] = useState("");
  const [scheduledList, setScheduledList] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [replyContext, setReplyContext] = useState(null);
  const { user: currentUser } = useAuth();

  const isPrivileged = currentUser && ["super_admin", "admin", "sales_manager"].includes(normalizeRole(currentUser.role));
  const isOwner = lead && currentUser && lead.lead_owner_id === currentUser.id;
  const canManage = isPrivileged || isOwner;

  const load = async () => {
    const { data } = await api.get(`/leads/${id}`);
    setLead(data);
    setEdits({});
  };

  const loadScheduled = async () => {
    try {
      const { data } = await api.get(`/scheduled-activities?lead_id=${id}`);
      setScheduledList(data);
    } catch (err) {
      console.warn("Failed to load scheduled activities:", err);
    }
  };

  useEffect(() => {
    (async () => {
      await load();
      const [a, h, au, m, u, s] = await Promise.all([
        api.get(`/leads/${id}/activities`),
        api.get(`/leads/${id}/history`),
        api.get(`/leads/${id}/audit`),
        api.get(`/masters?active_only=true`),
        api.get(`/users`).catch(() => ({ data: [] })),
        api.get(`/scheduled-activities?lead_id=${id}`).catch(() => ({ data: [] })),
      ]);
      setActivities(a.data); setHistory(h.data); setAudit(au.data);
      const grouped = {};
      m.data.forEach((it) => { grouped[it.type] = grouped[it.type] || []; grouped[it.type].push(it); });
      setMasters(grouped);
      setUsers(u.data || []);
      setScheduledList(s.data || []);
    })();

    // Fire-and-forget Gmail sync — runs independently of the main page paint so the
    // detail view feels instant. Throttled to once per 5 minutes per lead per tab to avoid
    // hammering the Gmail API every time the user re-opens the same lead.
    const SYNC_TTL_MS = 5 * 60 * 1000;
    const cacheKey = `gmail-sync-${id}`;
    const lastSync = Number(sessionStorage.getItem(cacheKey) || 0);
    if (Date.now() - lastSync > SYNC_TTL_MS) {
      sessionStorage.setItem(cacheKey, String(Date.now()));
      api.post(`/leads/${id}/sync-emails`)
        .then(async (r) => {
          if (r?.data?.synced > 0) {
            const a2 = await api.get(`/leads/${id}/activities`);
            setActivities(a2.data);
          }
        })
        .catch((err) => console.warn("Gmail sync failed:", err));
    }

  }, [id]);

  if (!lead) return <Layout title="Loading…"><div className="p-8 text-sm text-slate-500">Loading…</div></Layout>;

  const update = (k, v) => setEdits((e) => ({ ...e, [k]: v }));
  const value = (k) => (edits[k] !== undefined ? edits[k] : lead[k]);

  const save = async () => {
    if (Object.keys(edits).length === 0) return;
    // Seller Tag is mandatory to save any edit. Block if neither the edit nor
    // the stored lead has a valid value (1..5).
    // Seller Tag is mandatory to save only when the lead is in a seller-tag
    // stage (Agreement In-Process / Onboarding-in-Process / Live).
    if (SELLER_TAG_STAGES.includes(lead?.lead_stage)) {
      const st = value("seller_tag");
      if (!(Number(st) >= 1 && Number(st) <= 5)) {
        setSellerTagError("Please select a Seller Tag");
        toast.error("Please select a Seller Tag");
        return;
      }
    }
    setSellerTagError("");
    try {
      const { data } = await api.patch(`/leads/${id}`, edits);
      toast.success("Saved");
      const sso = data?._sso_sync?.toast;
      if (sso) { if (sso.level === "warning") toast.warning(sso.message); else toast.error(sso.message); }
      await load();
      const au = await api.get(`/leads/${id}/audit`);
      setAudit(au.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const pick = async () => {
    try {
      await api.post(`/leads/${id}/pick`);
      toast.success("Lead picked");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Pick failed");
    }
  };

  const logCall = async () => {
    try {
      await api.post(`/leads/${id}/call`, { status: callStatus, notes: callNote });
      toast.success("Call logged");
      setCallNote("");
      const a = await api.get(`/leads/${id}/activities`);
      setActivities(a.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/leads/${id}/note`, { text: noteText });
      toast.success("Note added");
      setNoteText("");
      const a = await api.get(`/leads/${id}/activities`);
      setActivities(a.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const reassign = async (newOwnerId) => {
    if (!newOwnerId) return;
    try {
      await api.post(`/leads/${id}/reassign`, { new_owner_id: newOwnerId });
      toast.success("Reassigned");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const renderInput = (key) => {
    const v = value(key);
    // Stage and lost_reason remain read-only (only changed via Move-stage dialog)
    if (key === "lead_stage" || key === "lost_reason") {
      return <div className="text-sm text-slate-700">{v || "—"}</div>;
    }
    // Tag (Hot/Warm/Cold/Lost) — editable inline
    if (key === "stage_tagging") {
      return (
        <select
          data-testid="field-stage-tagging"
          value={v || ""}
          onChange={(e) => update(key, e.target.value || null)}
          className="ld-input"
        >
          <option value="">— None —</option>
          {["Hot", "Warm", "Cold", "Lost"].map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      );
    }
    if (key === "opportunity_type") {
      const opts = masters.opp_type || [];
      const arr = Array.isArray(v) ? v : [];
      return (
        <div className="flex flex-wrap gap-1.5">
          {opts.map((o) => {
            const active = arr.includes(o.value);
            return (
              <button
                type="button"
                key={o.id}
                data-testid={`opp-type-${o.value.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() =>
                  update(
                    "opportunity_type",
                    active ? arr.filter((x) => x !== o.value) : [...arr, o.value],
                  )
                }
                className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                  active
                    ? "bg-orange-500 text-white border-orange-500"
                    : "bg-white text-slate-700 border-slate-200 hover:border-orange-300"
                }`}
              >
                {o.value}
              </button>
            );
          })}
          {opts.length === 0 && <span className="text-sm text-slate-400">No options</span>}
        </div>
      );
    }
    const masterKey = {
      category: "category", lead_source: "lead_source", sales_poc: "sales_poc",
      hq_state: "state", current_wh_partner: "wh_partner", current_lm_partner: "lm_partner",
      wms_oms: "wms_oms",
      utm_source: "utm_source", utm_medium: "utm_medium", utm_campaign: "utm_campaign",
      utm_content: "utm_content", utm_term: "utm_term",
    }[key];
    if (masterKey) {
      // For UTM fields, use a combobox (datalist) so admin-managed values are suggested
      // but free-text values from inbound API calls are still preserved/editable.
      if (key.startsWith("utm_")) {
        const listId = `dl-${key}`;
        return (
          <>
            <input
              data-testid={`field-${key}`}
              value={v || ""}
              onChange={(e) => update(key, e.target.value || null)}
              list={listId}
              className="ld-input"
              placeholder="Type or pick…"
            />
            <datalist id={listId}>
              {(masters[masterKey] || []).map((m) => <option key={m.id} value={m.value} />)}
            </datalist>
          </>
        );
      }
      return (
        <select value={v || ""} onChange={(e) => update(key, e.target.value)} className="ld-input">
          <option value="">—</option>
          {(masters[masterKey] || []).map((m) => <option key={m.id} value={m.value}>{m.value}</option>)}
        </select>
      );
    }
    if (key === "seller_tag") {
      return (
        <select
          data-testid="seller-tag-select"
          value={v ?? ""}
          onChange={(e) => { update(key, e.target.value === "" ? null : Number(e.target.value)); if (e.target.value) setSellerTagError(""); }}
          className="ld-input"
        >
          <option value="">Select seller tag</option>
          <option value="1">Brand</option>
          <option value="2">Magic</option>
          <option value="3">Drop-Shipper</option>
          <option value="4">Trader</option>
          <option value="5">Aggregator</option>
        </select>
      );
    }
    if (key === "remarks") {
      return <textarea rows={3} value={v || ""} onChange={(e) => update(key, e.target.value)} className="ld-input" />;
    }
    if (["monthly_order_potential", "overall_monthly_revenue", "universal_order_potential", "mg_amount", "monthly_shipping_volume"].includes(key)) {
      return <input type="number" value={v ?? ""} onChange={(e) => update(key, e.target.value === "" ? null : Number(e.target.value))} className="ld-input" />;
    }
    if (["interested_month", "tentative_live_month", "agreement_initiated_month", "oib_month", "live_month"].includes(key)) {
      return <input type="month" value={v || ""} onChange={(e) => update(key, e.target.value)} className="ld-input" />;
    }
    if (key === "lead_date") {
      // Stored as ISO timestamp — show only YYYY-MM-DD in the picker; save back as full ISO at UTC midnight.
      const ymd = v ? String(v).slice(0, 10) : "";
      return (
        <input
          data-testid="field-lead_date"
          type="date"
          value={ymd}
          onChange={(e) => {
            const d = e.target.value;
            update(key, d ? `${d}T00:00:00+00:00` : null);
          }}
          className="ld-input"
        />
      );
    }
    return <input value={v || ""} onChange={(e) => update(key, e.target.value)} className="ld-input" />;
  };

  return (
    <Layout title={lead.company_name || "Lead"} breadcrumb={["Leads", "Detail"]}>
      <style>{`.ld-input { width:100%; padding: 0.45rem 0.6rem; border:1px solid rgb(226 232 240); border-radius:0.375rem; font-size:0.85rem; background:white; outline:none; } .ld-input:focus{border-color:rgb(249 115 22);box-shadow:0 0 0 2px rgba(249,115,22,0.2)}`}</style>

      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 mb-3">
        <ArrowLeft size={14} /> Back
      </button>

      {/* Header card */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <StageBadge stage={lead.lead_stage} />
              <TagBadge tag={lead.stage_tagging} />
              {lead.is_pickable && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 text-xs font-medium border border-orange-200">
                  <Hand size={11} /> Pickable
                </span>
              )}
            </div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{lead.company_name}</h2>
            <div className="text-sm text-slate-500 mt-1">
              Owner: <span className="text-slate-700 font-medium">{lead.lead_owner_name || "—"}</span> • Last activity: {fmtDate(lead.last_activity_at)}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canManage && (
              <button
                data-testid="lead-move-stage-btn"
                onClick={() => setStageOpen(true)}
                className="text-sm font-medium px-3 py-1.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white flex items-center gap-1.5"
              >
                <MoveRight size={14} /> Move stage
              </button>
            )}
            {lead.is_pickable && (
              <button
                data-testid="lead-pick-btn"
                onClick={pick}
                className="text-sm font-medium px-3 py-1.5 rounded-md border border-orange-200 bg-orange-50 text-orange-700 flex items-center gap-1.5"
              >
                <Hand size={14} /> Pick
              </button>
            )}
            {canManage && (
              <button
                data-testid="lead-send-email-btn"
                onClick={() => { setReplyContext(null); setEmailOpen(true); }}
                className="text-sm font-medium px-3 py-1.5 rounded-md border border-orange-200 bg-white text-orange-700 hover:bg-orange-50 flex items-center gap-1.5"
              >
                <Send size={14} /> Send email
              </button>
            )}
            {canManage && (
              <details className="relative">
                <summary className="cursor-pointer list-none text-sm font-medium px-3 py-1.5 rounded-md border border-slate-200 bg-white flex items-center gap-1.5">
                  <UserCog size={14} /> Reassign
                </summary>
                <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-md p-2 w-56 z-10">
                  <select onChange={(e) => reassign(e.target.value)} className="ld-input">
                    <option value="">Choose owner…</option>
                    {users.filter((u) => u.active !== false).map((u) => (
                      <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                    ))}
                  </select>
                </div>
              </details>
            )}
            {!canManage && (
              <span data-testid="lead-readonly-badge" className="text-xs font-medium px-3 py-1.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200">
                Read-only — not your lead
              </span>
            )}
            {currentUser?.role === "super_admin" && (
              <button
                data-testid="lead-delete-btn"
                onClick={async () => {
                  const confirmText = `DELETE ${lead.company_name || "this lead"}`;
                  const entered = window.prompt(
                    `⚠ This will permanently delete this lead, ALL activities, scheduled tasks, and audit history. This cannot be undone.\n\nType the following to confirm:\n${confirmText}`
                  );
                  if (entered !== confirmText) {
                    if (entered !== null) toast.error("Confirmation didn't match — lead NOT deleted");
                    return;
                  }
                  try {
                    const { data } = await api.delete(`/leads/${lead.id}`);
                    toast.success(`Deleted "${data.company_name}" + ${data.deleted?.activities || 0} activities`);
                    navigate("/leads");
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Delete failed");
                  }
                }}
                className="text-sm font-medium px-3 py-1.5 rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-50 flex items-center gap-1.5"
                title="Permanently delete this lead (super-admin only)"
              >
                Delete lead
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 mb-5 shadow-sm">
        {[
          ["info", "Lead Info"],
          ["activities", "Activities"],
          ["history", "Stage History"],
          ["audit", "Audit Log"],
        ].map(([k, label]) => (
          <button
            key={k}
            data-testid={`tab-${k}`}
            onClick={() => setTab(k)}
            className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              tab === k ? "bg-orange-500 text-white" : "text-slate-700 hover:bg-slate-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "info" && (
        <div className="space-y-5">
          {SECTIONS.map((sec) => (
            <div key={sec.key} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800 mb-4 uppercase-track flex items-center justify-between">
                <span>{sec.title}</span>
                {sec.restricted && lead._financials_hidden && (
                  <span data-testid="financials-locked-badge" className="normal-case tracking-normal text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    🔒 Restricted — financial fields hidden for your role
                  </span>
                )}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {sec.fields.map((f) => {
                  if (lead._financials_hidden && FINANCIAL_FIELDS.has(f)) {
                    return (
                      <div key={f} data-testid={`field-locked-${f}`}>
                        <label className="block text-xs font-medium text-slate-500 mb-1">{LABELS[f] || f}</label>
                        <div className="text-sm text-slate-400 italic px-3 py-2 rounded-md bg-slate-50 border border-dashed border-slate-200">
                          Hidden
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={f}>
                      <label className="block text-xs font-medium text-slate-500 mb-1">
                        {LABELS[f] || f}{f === "seller_tag" && SELLER_TAG_STAGES.includes(lead?.lead_stage) && <span className="text-red-500"> *</span>}
                      </label>
                      {renderInput(f)}
                      {f === "seller_tag" && sellerTagError && (
                        <p data-testid="seller-tag-error" className="text-xs text-red-600 mt-1">{sellerTagError}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="flex justify-end pb-20">
            <button
              data-testid="lead-save-btn"
              onClick={save}
              disabled={Object.keys(edits).length === 0}
              className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-medium inline-flex items-center gap-1.5 shadow-sm"
            >
              <Save size={14} /> Save changes
            </button>
          </div>
          {/* Sticky save bar when there are unsaved changes — keeps button reachable above the Emergent watermark */}
          {Object.keys(edits).length > 0 && (
            <div
              data-testid="lead-sticky-save-bar"
              className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white border border-orange-200 shadow-lg rounded-full pl-4 pr-2 py-2 flex items-center gap-3"
            >
              <span className="text-xs text-slate-600">
                {Object.keys(edits).length} unsaved change{Object.keys(edits).length > 1 ? "s" : ""}
              </span>
              <button
                data-testid="lead-sticky-save-btn"
                onClick={save}
                className="px-3 py-1.5 text-sm rounded-full bg-orange-500 hover:bg-orange-600 text-white font-medium inline-flex items-center gap-1.5"
              >
                <Save size={13} /> Save
              </button>
              <button
                data-testid="lead-sticky-discard-btn"
                onClick={() => setEdits({})}
                className="px-2 py-1.5 text-xs text-slate-500 hover:text-slate-800"
              >
                Discard
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "activities" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Phone size={14} /> Log call</h3>
              <select value={callStatus} onChange={(e) => setCallStatus(e.target.value)} className="ld-input mb-2">
                <option value="connected">Connected</option>
                <option value="not_connected">Not connected</option>
                <option value="busy">Busy</option>
                <option value="rnr">Ring no response</option>
              </select>
              <textarea rows={3} value={callNote} onChange={(e) => setCallNote(e.target.value)} placeholder="Call notes…" className="ld-input mb-2" />
              <button data-testid="lead-log-call-btn" onClick={logCall} className="w-full text-sm font-medium px-3 py-2 rounded-md bg-orange-500 hover:bg-orange-600 text-white">Log call</button>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><MessageSquare size={14} /> Add note</h3>
              <textarea rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Type a note…" className="ld-input mb-2" />
              <button data-testid="lead-add-note-btn" onClick={addNote} className="w-full text-sm font-medium px-3 py-2 rounded-md bg-orange-500 hover:bg-orange-600 text-white">Add note</button>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><MoveRight size={14} /> Schedule activity</h3>
              <p className="text-xs text-slate-500 mb-2">Plan a call, meeting, email or follow-up for this lead.</p>
              <button
                data-testid="lead-schedule-activity-btn"
                onClick={() => setScheduleOpen(true)}
                className="w-full text-sm font-medium px-3 py-2 rounded-md bg-orange-500 hover:bg-orange-600 text-white"
              >
                + Schedule activity
              </button>
            </div>
          </div>
          <div className="lg:col-span-2 space-y-4">
            {scheduledList.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center justify-between">
                  <span>Planned activities ({scheduledList.filter((a) => a.status === "planned").length} open)</span>
                </h3>
                <ul className="space-y-2">
                  {scheduledList.slice(0, 8).map((s) => (
                    <li key={s.id} data-testid={`lead-scheduled-${s.id}`} className="flex items-center gap-3 p-2 rounded-md border border-slate-100 hover:bg-slate-50">
                      <button
                        data-testid={`lead-sched-toggle-${s.id}`}
                        onClick={async () => {
                          const next = s.status === "completed" ? "planned" : "completed";
                          try {
                            await api.patch(`/scheduled-activities/${s.id}`, { status: next });
                            await loadScheduled();
                            const a = await api.get(`/leads/${id}/activities`);
                            setActivities(a.data);
                          } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
                        }}
                        className={`w-5 h-5 shrink-0 rounded-full border-2 grid place-items-center ${s.status === "completed" ? "bg-emerald-500 border-emerald-500" : "border-slate-300 hover:border-emerald-400"}`}
                      >
                        {s.status === "completed" && <span className="text-white text-[10px]">✓</span>}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 capitalize">{s.type.replace(/_/g, " ")} • <span className="text-slate-500 font-normal">{new Date(s.scheduled_at).toLocaleString()}</span></div>
                        {s.notes && <div className="text-xs text-slate-500 truncate">{s.notes}</div>}
                        <div className="text-[11px] text-slate-400">For: {s.assignee_name}</div>
                      </div>
                      {s.status === "completed" && <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-600">Done</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <EmailThreadsView
              activities={activities}
              onReply={(ctx) => { setReplyContext(ctx); setEmailOpen(true); }}
            />
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">Activity timeline</h3>
              {activities.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No activities yet.</div>}
              <ul className="space-y-3">
                {activities.filter((a) => a.type !== "email_sent" && a.type !== "inbound_email").map((a) => (
                  <li key={a.id} className="border-l-2 border-orange-200 pl-4 pb-3">
                    <div className="text-xs uppercase tracking-wider text-orange-600 font-semibold">{a.type.replace(/_/g, " ")}</div>
                    <div className="text-sm text-slate-800 mt-0.5">
                      {a.type === "call" && <>Status: <b>{a.payload?.status}</b>. {a.payload?.notes}</>}
                      {a.type === "note" && a.payload?.text}
                      {a.type === "stage_change" && <>Moved {a.payload?.from || "—"} → <b>{a.payload?.to}</b> {a.payload?.tag && `(${a.payload.tag})`}</>}
                      {a.type === "reassign" && <>Reassigned to <b>{a.payload?.to}</b></>}
                      {a.type === "pick" && <>Picked from <b>{a.payload?.prev_owner || "—"}</b></>}
                      {a.type === "scheduled_completed" && <>Completed scheduled <b>{a.payload?.kind?.replace(/_/g, " ")}</b>. {a.payload?.notes}</>}
                      {a.type === "email_sent" && <>Sent to <b>{a.payload?.to}</b> — "{a.payload?.subject}"{a.payload?.auto_trigger && <span className="ml-1 text-[10px] font-semibold uppercase tracking-wider text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">Auto • {a.payload?.trigger_stage}</span>}{a.payload?.attachment_filenames?.length > 0 && <span className="ml-1 text-[10px] text-slate-500 inline-flex items-center gap-0.5">📎 {a.payload.attachment_filenames.join(", ")}</span>}</>}
                      {a.type === "inbound_email" && <>From <b>{a.payload?.from}</b> — "{a.payload?.subject || "(no subject)"}". {a.payload?.preview}</>}
                      {a.type === "stage_email_skipped" && (() => {
                        const reasonMap = {
                          gmail_not_connected: "neither the lead owner nor the user who moved the stage has Gmail connected",
                          no_sender_available: "no lead owner is assigned and the acting user is unknown",
                          lead_has_no_poc_email: "the lead has no POC email on file",
                          rendered_empty: "the template's subject/body was empty after variable substitution",
                          creds_failed: "Gmail credentials expired — please reconnect Gmail",
                        };
                        const r = a.payload?.reason || "unknown";
                        const friendly = reasonMap[r] || r;
                        return <span className="text-amber-700">Auto-trigger <b>"{a.payload?.template_name}"</b> for stage <b>{a.payload?.trigger_stage}</b> was skipped — {friendly}</span>;
                      })()}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{a.by_user_name} • {fmtDate(a.at)}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {tab === "history" && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Stage history</h3>
          <ol className="space-y-3">
            {history.map((h) => (
              <li key={h.id} className="flex gap-3 items-start">
                <div className="w-2.5 h-2.5 rounded-full bg-orange-500 mt-1.5 shrink-0" />
                <div>
                  <div className="text-sm text-slate-800">
                    {h.from_stage ? <>{h.from_stage} → <b>{h.to_stage}</b></> : <>Created in <b>{h.to_stage}</b></>}
                    {h.tag && <span className="ml-2 text-xs text-slate-500">tagged {h.tag}</span>}
                  </div>
                  <div className="text-[11px] text-slate-500">{h.by_user_name} • {fmtDate(h.at)}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {tab === "audit" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {["Field", "Old", "New", "By", "When"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {audit.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-slate-800 font-medium">{LABELS[row.field] || row.field}</td>
                  <td className="px-4 py-3 text-slate-500">{String(row.old ?? "—")}</td>
                  <td className="px-4 py-3 text-slate-800">{String(row.new ?? "—")}</td>
                  <td className="px-4 py-3 text-slate-600">{row.by_user_name}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(row.at)}</td>
                </tr>
              ))}
              {audit.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-sm text-slate-500">No changes recorded.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <StageTransitionDialog
        open={stageOpen}
        lead={lead}
        onClose={() => setStageOpen(false)}
        onMoved={async () => { setStageOpen(false); await load(); const [h, a, au] = await Promise.all([api.get(`/leads/${id}/history`), api.get(`/leads/${id}/activities`), api.get(`/leads/${id}/audit`)]); setHistory(h.data); setActivities(a.data); setAudit(au.data); }}
      />
      <ScheduleActivityDialog
        open={scheduleOpen}
        leadId={lead.id}
        leadCompany={lead.company_name}
        canAssignToOthers={isPrivileged}
        onClose={() => setScheduleOpen(false)}
        onSaved={() => { setScheduleOpen(false); loadScheduled(); }}
      />
      <SendEmailDialog
        open={emailOpen}
        lead={lead}
        currentUser={currentUser}
        replyContext={replyContext}
        onClose={() => { setEmailOpen(false); setReplyContext(null); }}
        onSent={async () => { setEmailOpen(false); setReplyContext(null); const a = await api.get(`/leads/${id}/activities`); setActivities(a.data); }}
      />
    </Layout>
  );
}


